import { useEffect, useRef, useState } from "react";
import { invalidarClientesLigeros } from "../lib/clientesLigeros.js";
import { PROVINCIAS_ES } from "../lib/provincias.js";

// Alta de cliente desde una cita o evento, con casilla "Nuevo".
// El alta solo se hace marcando la casilla, y exige un teléfono para poder
// contactar al cliente. El CIF/NIF y el resto de la ficha se completan
// después desde Clientes.
export default function AltaRapidaCliente({ nombreInicial = "", telefonoInicial = "", onCreado, onCambio }) {
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState(nombreInicial);
  const [telefono, setTelefono] = useState(telefonoInicial);
  const [nif, setNif] = useState("");
  const [provincia, setProvincia] = useState("");
  const [direccion, setDireccion] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  // Ref del aviso de cambios: el modal se entera del borrador en cada
  // cambio sin re-disparar el effect si el padre recrea la función.
  const aviso = useRef(onCambio);
  aviso.current = onCambio;
  useEffect(() => {
    aviso.current?.({ abierto, nombre: nombre.trim(), telefono: telefono.trim() });
  }, [abierto, nombre, telefono]);

  function alternar() {
    setNombre(nombreInicial);
    setTelefono(telefonoInicial);
    setNif("");
    setProvincia("");
    setDireccion("");
    setError(null);
    setAbierto((a) => !a);
  }

  async function crear() {
    if (!nombre.trim()) {
      setError("Escribe el nombre");
      return;
    }
    if (!telefono.trim()) {
      setError("Para dar de alta el cliente hay que indicar el teléfono");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const r = await fetch("/api/clientes/rapido", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: nombre.trim(),
          telefono: telefono.trim(),
          nif: nif.trim(),
          provincia: provincia.trim() || undefined,
          calle: direccion.trim() || undefined,
          exigirTelefono: true,
        }),
      });
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "No se pudo crear el cliente");
      invalidarClientesLigeros();
      onCreado?.(datos);
      setAbierto(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="mt-1">
      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={abierto}
          onChange={alternar}
          className="accent-[#2ec4b6]"
        />
        Cliente nuevo (dar de alta ahora)
      </label>
      {abierto && (
        <div className="mt-2 rounded-lg border border-teal-300 bg-teal-50 p-3 space-y-2">
          <p className="text-xs text-teal-700">
            El teléfono es obligatorio para el alta. El CIF/NIF se completa desde Clientes.
          </p>
          <input
            className="input w-full"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Nombre del cliente *"
          />
          <input
            className="input w-full"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="Teléfono *"
          />
          <input
            className="input w-full"
            value={nif}
            onChange={(e) => setNif(e.target.value.toUpperCase())}
            placeholder="CIF / NIF"
          />
          <input
            className="input w-full"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            placeholder="Dirección (calle, número, piso…)"
          />
          <input
            className="input w-full"
            value={provincia}
            onChange={(e) => setProvincia(e.target.value)}
            placeholder="Provincia"
            list="provincias-alta-rapida"
          />
          <datalist id="provincias-alta-rapida">
            {PROVINCIAS_ES.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
          {error && <p className="text-xs text-rose-600">{error}</p>}
          <button type="button" onClick={crear} disabled={guardando} className="btn-primary text-xs px-3 py-1.5">
            {guardando ? "Creando…" : "Dar de alta"}
          </button>
        </div>
      )}
    </div>
  );
}
